import { useEffect, useMemo, useRef, useState } from 'react';
import type { DeliveryBoardOrderDto, DeliveryStaffDto } from '@matix/api-client';
import { useLang } from '../../lib/language';
import { customerOf, orderNo, staffName } from './model';

/**
 * "Select driver and chef" — the legacy `selectDriverModal`, opened from the
 * Ready tab's "Delivery in progress" button and from the driver name on the
 * In-progress tab (re-assign).
 *
 * Drivers are the ones assigned to the ORDER's branch, as the legacy dialog
 * loaded them (`/Order/loaddrivers?BranchId=`). A driver is required — the
 * legacy page refused Confirm without one; a chef is optional and the whole
 * chef row disappears on a tenant with no Chef users, as legacy hid it.
 */
export function DispatchDialog({
  order,
  count,
  drivers,
  chefs,
  busy,
  onConfirm,
  onClose,
}: {
  order: DeliveryBoardOrderDto;
  /** Bulk use: how many selected orders go out with this driver (order = the first). */
  count?: number;
  drivers: DeliveryStaffDto[];
  chefs: DeliveryStaffDto[];
  busy: boolean;
  onConfirm: (driverId: number, chefId: number | null) => void;
  onClose: () => void;
}): JSX.Element {
  const { t, lang } = useLang();
  const [driverId, setDriverId] = useState<number | null>(order.driver_id);
  const [chefId, setChefId] = useState<number | null>(order.chef_id);
  const [q, setQ] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const branchDrivers = useMemo(() => {
    const own = drivers.filter((d) => d.branch_ids.includes(order.branch_id));
    return own.length > 0 ? own : drivers;
  }, [drivers, order.branch_id]);
  const fallback = branchDrivers === drivers && !drivers.some((d) => d.branch_ids.includes(order.branch_id));

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return branchDrivers;
    return branchDrivers.filter((d) =>
      [d.name, d.name_ar, d.code].some((v) => v != null && v.toLowerCase().includes(s)),
    );
  }, [branchDrivers, q]);

  const reassign = order.status === 3 && !count;

  return (
    <div className="cc-modal-backdrop" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cc-modal dm-modal dm-dispatch">
        <header className="cc-modal-head">
          <div>
            <div className="cc-modal-title">
              {reassign
                ? t({ en: 'Change driver', ar: 'تغيير السائق' })
                : t({ en: 'Select driver and chef', ar: 'اختر السائق والشيف' })}
            </div>
            <div className="cc-modal-sub">
              {count && count > 1
                ? t({ en: `${count} orders`, ar: `${count} طلبات` })
                : `${orderNo(order)} · ${customerOf(order, lang) || t({ en: 'Walk-in', ar: 'عميل نقدي' })}`}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            ×
          </button>
        </header>

        <input
          ref={searchRef}
          className="cc-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t({ en: 'Search driver by name or code', ar: 'ابحث عن السائق بالاسم أو الكود' })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && shown.length === 1) setDriverId(shown[0]!.id);
          }}
        />
        {fallback && drivers.length > 0 && (
          <div className="dm-hint">
            {t({ en: 'No driver is assigned to this branch — showing all drivers.', ar: 'لا يوجد سائق مرتبط بهذا الفرع — يتم عرض جميع السائقين.' })}
          </div>
        )}

        <div className="dm-driver-grid" role="listbox" aria-label={t({ en: 'Drivers', ar: 'السائقون' })}>
          {shown.map((d) => (
            <button
              key={d.id}
              type="button"
              role="option"
              aria-selected={driverId === d.id}
              className={`dm-driver ${driverId === d.id ? 'is-on' : ''}`}
              onClick={() => setDriverId(d.id)}
              onDoubleClick={() => !busy && onConfirm(d.id, chefId)}
            >
              <span className="dm-driver-avatar">{(staffName(d, lang) || '?').charAt(0).toUpperCase()}</span>
              <span className="dm-driver-name">{staffName(d, lang) || `#${d.id}`}</span>
              {d.code && <span className="dm-driver-code">{d.code}</span>}
            </button>
          ))}
          {shown.length === 0 && (
            <div className="dm-empty-inline">
              {drivers.length === 0
                ? t({ en: 'No drivers are set up. Add them in the admin app under Drivers.', ar: 'لا يوجد سائقون. أضفهم من تطبيق الإدارة ضمن السائقين.' })
                : t({ en: 'No driver matches.', ar: 'لا يوجد سائق مطابق.' })}
            </div>
          )}
        </div>

        {chefs.length > 0 && (
          <label className="dm-field">
            <span>{t({ en: 'Chef (optional)', ar: 'الشيف (اختياري)' })}</span>
            <select
              className="cc-select"
              value={chefId ?? ''}
              onChange={(e) => setChefId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{t({ en: 'Select chef', ar: 'اختر الشيف' })}</option>
              {chefs.map((c) => (
                <option key={c.id} value={c.id}>
                  {staffName(c, lang) || `#${c.id}`}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="dm-modal-actions">
          <button type="button" className="cc-btn" onClick={onClose}>
            {t({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button
            type="button"
            className="cc-btn cc-btn--primary"
            disabled={driverId == null || busy}
            onClick={() => driverId != null && onConfirm(driverId, chefId)}
          >
            {busy ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Confirm', ar: 'تأكيد' })}
          </button>
        </div>
      </div>
    </div>
  );
}
